'use client';

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { useAnalyticsContext } from '../analytics-context';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../../../components/ui/chart';
import { TrendingUp, TrendingDown } from 'lucide-react';

const chartConfig = {
  count: {
    label: 'Signups',
    color: 'var(--primary)',
  },
};

export function ChartUsers() {
  const { users, selectedFilter, payload } = useAnalyticsContext();

  const chartData =
    users.data?.data?.map((item: { label: string; count: number }) => ({
      label: item.label,
      count: item.count,
    })) ?? [];

  const { total, changePercent, changeValue } = useMemo(() => {
    if (!chartData.length) {
      return { total: 0, changePercent: 0, changeValue: 0 };
    }
    const totalUsers = users.data?.totalCount ?? 0;
    const first = chartData[0]?.count ?? 0;
    const last = chartData[chartData.length - 1]?.count ?? 0;
    const changeVal = last - first;
    const changePct = first === 0 ? 0 : (changeVal / first) * 100;
    return {
      total: totalUsers,
      changePercent: changePct,
      changeValue: changeVal,
    };
  }, [chartData, users.data?.totalCount]);

  const isAwaitingRange =
    selectedFilter === 'custom' && payload === null;

  return (
    <Card className="overflow-hidden border-none bg-gradient-to-br from-primary/5 via-background to-background shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            User Signups
          </CardTitle>
          <CardDescription className="mt-1 flex items-center gap-2 text-xs sm:text-sm">
            <span className="text-muted-foreground">Range:</span>
            <Badge variant="outline" className="capitalize">
              {selectedFilter === 'custom'
                ? 'Custom range'
                : selectedFilter.toLowerCase()}
            </Badge>
          </CardDescription>
        </div>
        <div className="flex items-end gap-3">
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Total signups</p>
            <p className="text-xl font-semibold text-foreground">
              {total.toLocaleString()}
            </p>
          </div>
          <Badge
            variant="secondary"
            className={`flex items-center gap-1 ${
              changePercent >= 0 ? 'text-emerald-600' : 'text-rose-600'
            }`}
          >
            {changePercent >= 0 ? (
              <TrendingUp className="h-3.5 w-3.5" />
            ) : (
              <TrendingDown className="h-3.5 w-3.5" />
            )}
            {`${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(1)}%`}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {users.isLoading ? (
          <div className="h-[300px] flex items-center justify-center">
            <div className="h-56 w-full animate-pulse rounded-xl bg-muted/60" />
          </div>
        ) : isAwaitingRange ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Select a start and end date to view custom data.
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No data available for the selected period
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="usersGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity={0.05} />
                  </linearGradient>
                  <filter id="usersGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="8" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid
                  strokeDasharray="4 4"
                  className="stroke-muted/40"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  className="text-muted-foreground text-xs"
                />

                <YAxis
                  tickMargin={8}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                  className="text-muted-foreground text-xs"
                  tickFormatter={value => value.toLocaleString()}
                />
                <ChartTooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={<ChartTooltipContent />}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="none"
                  fill="url(#usersGradient)"
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  dot={{
                    r: 4,
                    fill: 'var(--primary)',
                    strokeWidth: 2,
                    stroke: 'var(--primary)',
                  }}
                  activeDot={{
                    r: 7,
                    fill: 'var(--primary)',
                    strokeWidth: 2,
                    stroke: 'var(--primary)',
                    filter: 'url(#usersGlow)',
                  }}
                  name="Users"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
